/**
 * iter351 — Côté administration : demandes de l'atelier + fil de discussion.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import OrFil from "@/components/or/OrFil";

const CAT_LABEL = { devis: "Devis demandé", pieces: "Pièces demandées", validation: "Validation demandée" };

export const OrAtelierBlock = ({ orId, onTraite }) => {
  const [demandes, setDemandes] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/or/${orId}/demandes`);
    setDemandes(data.items || []);
  }, [orId]);

  useEffect(() => { load(); }, [load]);

  const traiter = async (d) => {
    setBusy(d.id);
    try {
      await api.post(`/demandes/${d.id}/traiter`, { reponse: null });
      toast.success("Demande close, l'atelier est informé");
      await load();
      onTraite?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible");
    } finally {
      setBusy(null);
    }
  };

  const ouvertes = demandes.filter((d) => d.statut === "ouverte");

  return (
    <Card className="p-4 border-gray-200" data-testid="or-atelier-block">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="w-4 h-4 text-[#52a675]" />
        <h3 className="font-heading font-semibold text-gray-900">Atelier</h3>
        {ouvertes.length > 0 && (
          <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-300" data-testid="or-demandes-count">
            {ouvertes.length} demande{ouvertes.length > 1 ? "s" : ""} à traiter
          </Badge>
        )}
      </div>

      {ouvertes.length > 0 && (
        <div className="space-y-2 mb-4">
          {ouvertes.map((d) => (
            <div key={d.id} className="p-3 rounded-xl border-2 border-orange-200 bg-orange-50/50" data-testid={`or-demande-${d.id}`}>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px] bg-white">{CAT_LABEL[d.categorie] || d.categorie}</Badge>
                <span className="text-[11px] text-gray-500">par {d.demandeur_nom}</span>
              </div>
              {d.texte && <div className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{d.texte}</div>}
              {(d.articles || []).length > 0 && (
                <ul className="mt-1 text-xs text-gray-700 list-disc list-inside">
                  {d.articles.map((a, i) => (
                    <li key={i}>{a.libelle}{a.quantite ? ` × ${a.quantite}` : ""}</li>
                  ))}
                </ul>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[11px] text-gray-500 flex-1">
                  Réponds dans la conversation ci-dessous : l'attente se lève toute seule.
                </span>
                <Button
                  variant="outline" onClick={() => traiter(d)} disabled={busy === d.id}
                  className="h-8 text-xs border-gray-300 flex-shrink-0"
                  data-testid={`or-demande-traiter-${d.id}`}
                >
                  {busy === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Clore</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <OrFil orId={orId} compact onChanged={load} />
    </Card>
  );
};

export default OrAtelierBlock;
